import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button, Card } from '@/components/ui';
import { formatDate, formatTime } from '@/lib/utils/date';
import { routePaths } from '@/app/routes';
import { getPrice } from '@/lib/nomenclature/appointmentRepo';
import { useActivePatient } from '@/features/patients/hooks/useActivePatient';
import { appointmentsQueryKey } from '../hooks/useAppointments';
import { setAppointment, SET_APPOINTMENT_MESSAGES } from '../api/booking.api';
import type { UseBooking } from '../booking/useBooking';
import type { ResolvedSlot } from '../booking/types';

interface BookingConfirmationProps {
  booking: UseBooking;
  slot: ResolvedSlot;
  onBack: () => void;
}

/** Step 2 — readonly summary, patient + note, then `SetAppointment`. */
export function BookingConfirmation({ booking, slot, onBack }: BookingConfirmationProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { patient, patients, setActivePatient } = useActivePatient();
  const [note, setNote] = useState('');

  const { selection } = booking;

  const { data: price } = useQuery({
    queryKey: [
      'booking-price',
      slot.physicianDbId,
      selection.specialty?.id,
      selection.location?.id,
      slot.service?.id,
    ],
    queryFn: () =>
      getPrice({
        physicianId: slot.physicianDbId!,
        specialtyId: selection.specialty!.id,
        locationId: selection.location!.id,
        invId: slot.service?.id,
      }),
    enabled: slot.physicianDbId != null && !!selection.specialty && !!selection.location,
  });

  const mutation = useMutation({
    mutationFn: () =>
      setAppointment({
        patientId: patient!.fhirId,
        physicianId: slot.physicianId,
        entityId: slot.entityId,
        locationId: selection.location!.locationId,
        investigationId: slot.service?.invId ?? null,
        timeFrom: slot.start,
        timeTo: slot.end,
        appointmentNote: note.trim(),
        firstAvailable: booking.mode === 'first',
      }),
    onSuccess: (result) => {
      if (result.success) {
        void queryClient.invalidateQueries({ queryKey: appointmentsQueryKey(patient!.fhirId) });
      }
    },
  });

  const result = mutation.data;

  // Terminal success screen.
  if (result?.success) {
    return (
      <Card className="flex flex-col items-center gap-4 p-8 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-success-light text-success">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M20 6 9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <div>
          <p className="text-lg font-bold text-ink">{t('booking.success_title')}</p>
          <p className="mt-1 text-sm text-ink-mute">{SET_APPOINTMENT_MESSAGES[result.state]}</p>
        </div>
        <Button onClick={() => navigate(routePaths.appointments)} block>
          {t('booking.go_appointments')}
        </Button>
      </Card>
    );
  }

  const summary: Array<[string, string]> = [
    [t('booking.summary_doctor'), slot.doctorName],
    [t('booking.summary_specialty'), selection.specialty?.name ?? '-'],
    [t('booking.summary_service'), slot.serviceName],
    [t('booking.summary_datetime'), `${formatDate(slot.day)} · ${formatTime(slot.start)} – ${formatTime(slot.end)}`],
    [t('booking.summary_location'), slot.locationName],
    [t('booking.summary_price'), price != null ? `${price} RON` : t('booking.price_unspecified')],
  ];

  return (
    <div className="flex flex-col gap-5">
      <Card className="flex flex-col gap-4 p-5">
        <h2 className="text-base font-bold text-ink">{t('booking.confirm_title')}</h2>
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
          {summary.map(([label, value]) => (
            <div key={label} className="contents">
              <dt className="text-ink-mute">{label}</dt>
              <dd className="text-right font-semibold text-ink">{value}</dd>
            </div>
          ))}
        </dl>
      </Card>

      <Card className="flex flex-col gap-4 p-5">
        {/* Patient picker */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-semibold text-ink-soft">{t('booking.patient')}</label>
          <select
            value={patient?.fhirId ?? ''}
            onChange={(e) => setActivePatient(e.target.value)}
            className="h-11 w-full rounded-input border border-muted-300 bg-white px-3 text-sm text-ink focus-visible:border-primary focus-visible:outline-none"
          >
            {patients.map((p) => (
              <option key={p.fhirId} value={p.fhirId}>
                {p.fullName}
              </option>
            ))}
          </select>
        </div>

        {/* Optional note */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-semibold text-ink-soft">{t('booking.note')}</label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder={t('booking.note_placeholder')}
            className="w-full rounded-input border border-muted-300 bg-white px-3 py-2 text-sm text-ink focus-visible:border-primary focus-visible:outline-none"
          />
        </div>
      </Card>

      {result && !result.success && (
        <p className="rounded-input bg-danger-light px-4 py-3 text-sm font-semibold text-danger">
          {SET_APPOINTMENT_MESSAGES[result.state]}
        </p>
      )}
      {mutation.isError && (
        <p className="rounded-input bg-danger-light px-4 py-3 text-sm font-semibold text-danger">
          {t('booking.confirm_error')}
        </p>
      )}

      <div className="flex gap-3">
        <Button variant="outline" onClick={onBack} disabled={mutation.isPending}>
          {t('common.back')}
        </Button>
        <Button
          onClick={() => mutation.mutate()}
          loading={mutation.isPending}
          disabled={!patient}
          block
        >
          {t('booking.confirm')}
        </Button>
      </div>
    </div>
  );
}
